import { reactive } from "vue";

import type {
  AiRichCitation,
  ConversationId,
  Document,
  OpenaiMessage,
} from "@ctrl/controller-rpc";

export type PendingAssistantMessage = {
  role: "assistant";
  content: string;
  document: Document;
  citations?: AiRichCitation[];
  tool_calls?: never;
};

export type PendingMessage = OpenaiMessage | PendingAssistantMessage;

export type ToolCallGroupState = {
  isGroupExpanded: () => boolean;
  toggleGroup: () => void;
  isCallExpanded: (callIdx: number) => boolean;
  toggleCall: (callIdx: number) => void;
  reset: () => void;
};

type ToolCallGroupStateData = {
  expanded: boolean;
  expandedCalls: Record<number, boolean>;
};

export function createToolCallGroupState(): ToolCallGroupState {
  const state = reactive<ToolCallGroupStateData>({
    expanded: false,
    expandedCalls: {},
  });
  return {
    isGroupExpanded: () => state.expanded,
    toggleGroup: () => {
      state.expanded = !state.expanded;
    },
    isCallExpanded: (callIdx) => !!state.expandedCalls[callIdx],
    toggleCall: (callIdx) => {
      state.expandedCalls[callIdx] = !state.expandedCalls[callIdx];
    },
    reset: () => {
      state.expanded = false;
      state.expandedCalls = {};
    },
  };
}

/**
 * Per-conversation state for an in-flight (or just-completed) agent send.
 *
 * This state is module-level (rather than component-level) so it survives
 * navigating away from the conversation while the agent is "thinking" and
 * coming back: without it, the optimistic user message and thinking indicator
 * would be lost when AgentConversation unmounts, and the page would appear
 * blank on return until the LLM response finally lands in the server history.
 */
export type InFlightSendState = {
  /**
   * Local messages appended to history until the controller's conversation
   * ref catches up with the send.
   */
  pendingMessages: PendingMessage[];
  /** History length before this send started, used to drop reconciled pending messages. */
  historyLengthAtStart: number;
  /** True while a send is currently in flight for this conversation. */
  isSending: boolean;
  /** Expansion state shared by the live and final tool-call groups. */
  toolCallState: ToolCallGroupState;
  /** Error from the most recent send, if any. */
  errorMessage: string | null;
};

const states = reactive(new Map<ConversationId, InFlightSendState>());

function ensure(conversationId: ConversationId): InFlightSendState {
  let s = states.get(conversationId);
  if (!s) {
    s = {
      pendingMessages: [],
      historyLengthAtStart: 0,
      isSending: false,
      toolCallState: createToolCallGroupState(),
      errorMessage: null,
    };
    states.set(conversationId, s);
  }
  return s;
}

export function getSendState(
  conversationId: ConversationId
): InFlightSendState | undefined {
  return states.get(conversationId);
}

/** True while a send is currently in flight for this conversation. */
export function isSendInFlight(conversationId: ConversationId): boolean {
  return states.get(conversationId)?.isSending === true;
}

export function startSend(
  conversationId: ConversationId,
  optimisticUserMessage: OpenaiMessage,
  historyLengthAtStart: number
): void {
  const s = ensure(conversationId);
  s.pendingMessages = [optimisticUserMessage];
  s.historyLengthAtStart = historyLengthAtStart;
  s.isSending = true;
  s.toolCallState.reset();
  s.errorMessage = null;
}

export function finishSend(
  conversationId: ConversationId,
  document: Document,
  citations: AiRichCitation[]
): void {
  const s = states.get(conversationId);
  if (!s) return;
  s.isSending = false;
  s.pendingMessages = [
    ...s.pendingMessages,
    {
      role: "assistant",
      content: "",
      document,
      ...(citations.length > 0 ? { citations } : {}),
    },
  ];
}

export function failSend(
  conversationId: ConversationId,
  errorMessage: string
): void {
  const s = states.get(conversationId);
  if (!s) return;
  s.pendingMessages = [];
  s.isSending = false;
  s.errorMessage = errorMessage;
}

/**
 * Drop any stored citations/errors once the caller has reconciled them with
 * the server history. No-op if there is no entry.
 */
export function clearSendState(conversationId: ConversationId): void {
  states.delete(conversationId);
}
